import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import Overlay from './Overlay'
import MessageBox from './MessageBox'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const FuelProofUploadModal = ({
  open,
  onClose,
  onUploaded
}) => {
  const { t, i18n } = useTranslation()

  const [file, setFile] = useState(null)
  const [fileError, setFileError] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [message, setMessage] = useState(null)

  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)

  useEffect(() => {
    if (!open) return

    setFile(null)
    setFileError(false)
    setMessage(null)
    setPressedBtn(null)
    setHoverBtn(null)
  }, [open])

  useEffect(() => {
    const handleKeyDown = e => {
      if (
        e.key === 'Escape' &&
        open &&
        !uploading
      ) {
        onClose()
      }
    }

    window.addEventListener(
      'keydown',
      handleKeyDown
    )

    return () => {
      window.removeEventListener(
        'keydown',
        handleKeyDown
      )
    }
  }, [
    open,
    uploading,
    onClose
  ])

  const handleFileSelected = e => {
    const selectedFile =
      e.target.files?.[0] || null

    setFile(selectedFile)
    setFileError(false)
    setMessage(null)
  }

  const saveUpload = async () => {
    if (!file) {
      setFileError(true)
      return
    }

    try {
      setUploading(true)
      setMessage(null)

      const fd = new FormData()

      fd.append(
        'file',
        file
      )

      const res =
        await fetchWithAuth(
          '/triplog/receipts/fuel-proof',
          {
            method: 'POST',
            body: fd
          },
          i18n.language
        )

      if (!res.ok) {
        setMessage({
          type: 'error',
          message: t(
            'TRIPLOG.RECEIPTS.INVALID_PROOF_FORMAT_MSG'
          )
        })

        return
      }

      const data =
        await res.json()

      if (!data?.upload_id) {
        setMessage({
          type: 'error',
          message: t(
            'TRIPLOG.RECEIPTS.INVALID_PROOF_FORMAT_MSG'
          )
        })

        return
      }

      onUploaded?.(
        data.upload_id
      )
    } catch {
      setMessage({
        type: 'error',
        message:
          t('CONNECTION_ERROR')
      })
    } finally {
      setUploading(false)
    }
  }

  if (!open) return null

  return (
    <>
      <Overlay
        open={uploading}
        zIndex={5000}
        backdropOpacity={0.75}
      />

      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor:
            'rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 4100
        }}
      >
        <div
          onClick={e =>
            e.stopPropagation()
          }
          style={{
            background: '#ededed',
            padding: 20,
            border:
              '2px solid #107c6b',
            borderRadius: 12,
            boxShadow:
              '0 12px 28px rgba(0,0,0,0.65)',
            width:
              'clamp(320px, 30vw, 420px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <h3
            style={{
              fontSize: 20,
              marginBottom: 16,
              textAlign: 'center'
            }}
          >
            {t(
              'TRIPLOG.VEHICLES.BUTTON_UPLOAD_RECEIPT_BUTTON'
            )}
          </h3>

          <input
            id="fuel-proof-upload-input"
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={handleFileSelected}
            style={{
              display: 'none'
            }}
          />

          <button
            type="button"
            onClick={() => {
              document
                .getElementById(
                  'fuel-proof-upload-input'
                )
                ?.click()
            }}
            style={{
              border:
                fileError
                  ? '1.5px solid #c33'
                  : '1.5px solid #107c6b',
              background:
                fileError
                  ? '#f6caca'
                  : '#f5f5f5',
              borderRadius: 6,
              height: 28,
              padding: '0 16px',
              cursor: 'pointer',
              fontWeight: 700,
              marginBottom: 12
            }}
          >
            {t('vehicle_select_file')}
          </button>

          <span
            style={{
              fontSize: 14,
              color:
                file
                  ? '#107c6b'
                  : '#888',
              marginBottom: 16
            }}
          >
            {file
              ? file.name
              : t('vehicle_no_file')
            }
          </span>

          {message && (
            <div
              style={{
                width: '100%',
                marginBottom: 14
              }}
            >
              <MessageBox
                message={message.message}
                type={message.type}
              />
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: 12
            }}
          >
            <button
              style={
                modalPrimaryButtonStyle(
                  pressedBtn,
                  'saveFuelProof',
                  hoverBtn ===
                    'saveFuelProof'
                )
              }
              onMouseEnter={() =>
                setHoverBtn(
                  'saveFuelProof'
                )
              }
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() =>
                setPressedBtn(
                  'saveFuelProof'
                )
              }
              onMouseUp={() =>
                setPressedBtn(null)
              }
              onClick={saveUpload}
            >
              {t('SAVE')}
            </button>

            <button
              style={
                modalSecondaryButtonStyle(
                  pressedBtn,
                  'cancelFuelProof',
                  hoverBtn ===
                    'cancelFuelProof'
                )
              }
              onMouseEnter={() =>
                setHoverBtn(
                  'cancelFuelProof'
                )
              }
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() =>
                setPressedBtn(
                  'cancelFuelProof'
                )
              }
              onMouseUp={() =>
                setPressedBtn(null)
              }
              onClick={onClose}
            >
              {t('CANCEL')}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default FuelProofUploadModal